import { memo } from 'react'
import { Pressable, StyleSheet } from 'react-native'
import Animated, {
  type WithSpringConfig,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated'

import { DEPOSIT_METRICS } from '../../screens/depositMetrics'
import { useHapticFeedback } from '../../haptics/HapticsContext'
import { DepositChevronIcon } from '../../icons/DepositChevronIcon'
import { Box, Text, useTheme } from '../../theme/restyle'
import type { NumpadKey } from '../../utils/numpadInput'

// Shared with the delete glyph, which sizes to the digits.
const KEY_FONT_SIZE = DEPOSIT_METRICS.numpad.fontSize

// The design sets the outer columns against the keypad's edges. The touch
// target is still the whole share.
const COLUMN_ALIGNMENT = ['flex-start', 'center', 'flex-end'] as const

// The keys scale up to meet the touch, then come to restitution with subtle
// springiness (Figma: Typing Input). Up is instant-feeling — it must land with
// the finger — so it's a short timing; the settle is the spring.
const KEY_PRESS_SCALE = 2.3
const KEY_PRESS_IN_DURATION = 80
const KEY_RELEASE_SPRING: WithSpringConfig = {
  damping: 14,
  stiffness: 320,
  mass: 0.5,
}

const ROWS: NumpadKey[][] = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', 'backspace'],
]

// Cells meet halfway between glyphs; only the outer edges extend their hit area.
const OUTER_HIT_SLOP = 16

interface KeyHitSlop {
  top: number
  bottom: number
  left: number
  right: number
}

function keyHitSlop(row: number, column: number, lastRow: number): KeyHitSlop {
  return {
    top: row === 0 ? OUTER_HIT_SLOP : 0,
    bottom: row === lastRow ? OUTER_HIT_SLOP : 0,
    left: column === 0 ? OUTER_HIT_SLOP : 0,
    right: column === 2 ? OUTER_HIT_SLOP : 0,
  }
}

const AnimatedBox = Animated.createAnimatedComponent(Box)

export interface NumPadProps {
  onPressKey: (key: NumpadKey) => void
  /** The delete key carries a glyph, so its label can't come from the key. */
  backspaceAccessibilityLabel: string
  /** Makes every keypad control disabled and non-interactive. */
  disabled?: boolean
}

/**
 * Emits keys rather than a value: `applyNumpadKey` owns the grammar.
 */
export const NumPad = memo(function NumPad({
  onPressKey,
  backspaceAccessibilityLabel,
  disabled = false,
}: NumPadProps) {
  const theme = useTheme()
  const haptic = useHapticFeedback()

  return (
    <Box flex={1} style={styles.grid}>
      {ROWS.map((row, rowIndex) => (
        <Box key={row.join('')} flexDirection="row" flex={1}>
          {row.map((key, column) => (
            <NumPadKey
              key={key}
              numpadKey={key}
              hitSlop={keyHitSlop(rowIndex, column, ROWS.length - 1)}
              alignment={COLUMN_ALIGNMENT[column]}
              deleteColor={theme.content.elements.primary}
              accessibilityLabel={
                key === 'backspace' ? backspaceAccessibilityLabel : key
              }
              disabled={disabled}
              onPress={() => {
                // Before the handler: the tap should land with the finger, not
                // after whatever the amount change costs.
                haptic()
                onPressKey(key)
              }}
              onLongPress={
                key === 'backspace'
                  ? () => {
                      haptic()
                      onPressKey('clear')
                    }
                  : undefined
              }
            />
          ))}
        </Box>
      ))}
    </Box>
  )
})

interface NumPadKeyProps {
  numpadKey: NumpadKey
  hitSlop: KeyHitSlop
  alignment: (typeof COLUMN_ALIGNMENT)[number]
  deleteColor: string
  accessibilityLabel: string
  disabled: boolean
  onPress: () => void
  /**
   * Its presence alone makes the Pressable withhold `onPress` from a held
   * touch, so digit keys omit it: an accidental hold still enters the digit.
   */
  onLongPress?: () => void
}

// Memoised: the pad re-renders only when its (stable) handler changes, and
// then only keys whose props changed re-render.
const NumPadKey = memo(function NumPadKey({
  numpadKey,
  hitSlop,
  alignment,
  deleteColor,
  accessibilityLabel,
  disabled,
  onPress,
  onLongPress,
}: NumPadKeyProps) {
  const scale = useSharedValue(1)
  const pressScaling = useAnimatedStyle(() => {
    // Spelled out because the plugin workletizes by the hook's name, and esbuild
    // renames this one in the bundle (see AmountDisplay).
    'worklet'
    return { transform: [{ scale: scale.value }] }
  })

  // Up lands with the finger (a short timing); the release settles on the
  // spring (see the constants above).
  const handlePressIn = () => {
    scale.value = withTiming(KEY_PRESS_SCALE, {
      duration: KEY_PRESS_IN_DURATION,
    })
  }
  const handlePressOut = () => {
    scale.value = withSpring(1, KEY_RELEASE_SPRING)
  }

  return (
    <Pressable
      disabled={disabled}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onPress={onPress}
      onLongPress={onLongPress}
      hitSlop={hitSlop}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled }}
      style={[styles.key, { alignItems: alignment }]}
    >
      {/* Only the glyph scales, never the cell: a growing Pressable would move
          the touch boundaries mid-press. */}
      <AnimatedBox style={pressScaling}>
        {numpadKey === 'backspace' ? (
          <DepositChevronIcon color={deleteColor} />
        ) : (
          <Text
            weight="medium"
            style={[
              styles.label,
              numpadKey === '.' ? styles.decimalLabel : null,
            ]}
          >
            {numpadKey}
          </Text>
        )}
      </AnimatedBox>
    </Pressable>
  )
})

const styles = StyleSheet.create({
  // Figma bounds the glyph rows; touch cells extend half a gap beyond them.
  grid: {
    marginVertical:
      (DEPOSIT_METRICS.numpad.lineHeight - DEPOSIT_METRICS.numpad.rowPitch) / 2,
  },
  key: { flex: 1, justifyContent: 'center' },
  label: {
    fontSize: KEY_FONT_SIZE,
    lineHeight: 32,
    letterSpacing: DEPOSIT_METRICS.numpad.tracking,
    textAlign: 'center',
  },
  decimalLabel: {
    fontSize: DEPOSIT_METRICS.numpad.decimalSize,
    letterSpacing: DEPOSIT_METRICS.numpad.decimalTracking,
    lineHeight: 56,
    transform: [{ translateY: DEPOSIT_METRICS.numpad.decimalOffsetY }],
  },
})
